import { useCallback, useState } from "react";

export type SpeedTarget = "internet" | "fcm" | "truenas";

export type SpeedResult = {
  status: "idle" | "running" | "success" | "error";
  summary: string;
  detail: string;
  updatedAt?: string;
};

type SpeedState = Record<SpeedTarget, SpeedResult>;

const idleState: SpeedState = {
  internet: { status: "idle", summary: "未測定", detail: "現在地からCloudflare edgeまで" },
  fcm: { status: "idle", summary: "未測定", detail: "https://fcm.plus の応答時間" },
  truenas: { status: "idle", summary: "未測定", detail: "TrueNAS Tailscale/LAN UIの応答時間" }
};

const targetLabels: Record<SpeedTarget, string> = {
  internet: "回線速度",
  fcm: "fcm.plus",
  truenas: "TrueNAS"
};

const withTimeout = async <T>(task: (signal: AbortSignal) => Promise<T>, timeoutMs: number): Promise<T> => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await task(controller.signal);
  } finally {
    window.clearTimeout(timer);
  }
};

const timedFetch = async (url: string, init: RequestInit = {}, timeoutMs = 6000) => {
  const start = performance.now();
  const response = await withTimeout(
    (signal) =>
      fetch(url, {
        cache: "no-store",
        ...init,
        signal
      }),
    timeoutMs
  );
  return { response, ms: performance.now() - start };
};

const formatMs = (ms: number) => `${Math.max(1, Math.round(ms))} ms`;

const formatMbps = (bytes: number, ms: number) => {
  const mbps = (bytes * 8) / (ms / 1000) / 1_000_000;
  return `${mbps.toFixed(mbps >= 100 ? 0 : 1)} Mbps`;
};

const makeUploadBody = (bytes: number) => {
  const body = new Uint8Array(bytes);
  for (let index = 0; index < body.length; index += 1) {
    body[index] = (index * 17 + 91) % 251;
  }
  return body;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof DOMException && error.name === "AbortError") return "timeout";
  if (error instanceof Error) return error.message;
  return "測定失敗";
};

export function useSpeedTests() {
  const [results, setResults] = useState<SpeedState>(idleState);

  const setTarget = useCallback((target: SpeedTarget, next: SpeedResult) => {
    setResults((current) => ({ ...current, [target]: next }));
  }, []);

  const runInternet = useCallback(async () => {
    const target: SpeedTarget = "internet";
    setTarget(target, { status: "running", summary: "測定中...", detail: "ping/down/upを実行中" });
    try {
      const pingSamples: number[] = [];
      for (let index = 0; index < 3; index += 1) {
        const { ms } = await timedFetch(`/api/speed/ping?sample=${index}&t=${Date.now()}`, {}, 4500);
        pingSamples.push(ms);
      }

      const downloadBytes = 4 * 1024 * 1024;
      const download = await timedFetch(`/api/speed/download?bytes=${downloadBytes}&t=${Date.now()}`, {}, 10000);
      const downloadBuffer = await download.response.arrayBuffer();

      const uploadBody = makeUploadBody(1024 * 1024);
      const upload = await timedFetch(
        `/api/speed/upload?t=${Date.now()}`,
        { method: "POST", body: uploadBody, headers: { "content-type": "application/octet-stream" } },
        10000
      );
      if (!upload.response.ok) throw new Error(`upload HTTP ${upload.response.status}`);

      const ping = pingSamples.reduce((sum, value) => sum + value, 0) / pingSamples.length;
      setTarget(target, {
        status: "success",
        summary: `↓ ${formatMbps(downloadBuffer.byteLength, download.ms)} / ↑ ${formatMbps(uploadBody.byteLength, upload.ms)}`,
        detail: `Cloudflare edge ${formatMs(ping)}`,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      setTarget(target, {
        status: "error",
        summary: "測定失敗",
        detail: getErrorMessage(error),
        updatedAt: new Date().toISOString()
      });
    }
  }, [setTarget]);

  const runOpaqueLatency = useCallback(
    async (target: SpeedTarget, urls: string[], timeoutMs: number) => {
      setTarget(target, { status: "running", summary: "測定中...", detail: targetLabels[target] });
      const errors: string[] = [];

      for (const url of urls) {
        try {
          const separator = url.includes("?") ? "&" : "?";
          const testUrl = `${url}${separator}labSpeed=${Date.now()}`;
          const { ms } = await timedFetch(testUrl, { mode: "no-cors" }, timeoutMs);
          setTarget(target, {
            status: "success",
            summary: formatMs(ms),
            detail: new URL(url).host,
            updatedAt: new Date().toISOString()
          });
          return;
        } catch (error) {
          errors.push(`${new URL(url).host}: ${getErrorMessage(error)}`);
        }
      }

      setTarget(target, {
        status: "error",
        summary: "到達不可",
        detail: errors[0] ?? "ブラウザから到達できません",
        updatedAt: new Date().toISOString()
      });
    },
    [setTarget]
  );

  const runFcm = useCallback(() => runOpaqueLatency("fcm", ["https://fcm.plus/"], 6500), [runOpaqueLatency]);

  const runTruenas = useCallback(
    () =>
      runOpaqueLatency(
        "truenas",
        [
          "https://truenas-scale.tailed53cd.ts.net/ui/",
          "https://100.99.77.49/ui/",
          "https://172.16.30.101/ui/"
        ],
        8000
      ),
    [runOpaqueLatency]
  );

  const run = useCallback(
    (target: SpeedTarget) => {
      if (target === "internet") void runInternet();
      if (target === "fcm") void runFcm();
      if (target === "truenas") void runTruenas();
    },
    [runFcm, runInternet, runTruenas]
  );

  return { results, run };
}
